import { Route as RouteIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { modeLabelKey } from '@/lib/icons'
import type { CaseDetail, RouteStop, RouteStopState } from '@/types/domain'

const STOP_DOT: Record<RouteStopState, string> = {
  passed: 'bg-positive',
  current: 'bg-attention',
  ahead: 'border-neutral-300 bg-surface border-[1.5px]',
}

/** The case route across the top: POL, border crossing, POD, unloading place,
 *  each captioned with what it is and where the shipment stands against it.
 *  The state words come from the SERVER — "here now" is never re-derived here
 *  from leg statuses, so the route and the stuck flag cannot disagree. Colour
 *  on the dots restates the caption, never replaces it. */
export function RouteCard({ record }: { record: CaseDetail }) {
  const { t } = useTranslation()
  const format = useFormat()
  const stops = record.route ?? []

  /* "rail + road via Khorgos" — the modes in leg order, deduplicated, and the
     border stop by name. Derived rather than served: it restates facts already
     in this response, in one breath. */
  const modes = record.legs.length
    ? [...new Set(record.legs.map((leg) => leg.mode))]
    : [record.mode]
  const modeLine = modes
    .map((mode) => t(modeLabelKey(mode)).toLocaleLowerCase())
    .join(' + ')
  const border = stops.find((stop) => stop.role === 'border')

  const status = (stop: RouteStop): string => {
    switch (stop.state) {
      case 'passed':
        /* The origin was LEFT, every later passed stop was reached — two
           different facts, two different words. */
        return stop.role === 'loading'
          ? t('cases.overview.departed', { date: format.dateShort(stop.date) })
          : t('cases.overview.arrivedOn', { date: format.dateShort(stop.date) })
      case 'current':
        return stop.dwellDays != null
          ? t('cases.overview.hereNowFor', { days: stop.dwellDays })
          : t('cases.overview.hereNow')
      case 'ahead':
        return t('cases.overview.etaOn', { date: format.dateShort(stop.date) })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="flex items-center gap-2">
            <RouteIcon size={15} className="text-text-muted" aria-hidden />
            {t('cases.overview.route')}
          </span>
          <span className="text-text-muted text-[12.5px] font-light">
            {border
              ? t('cases.overview.routeVia', { modes: modeLine, border: border.name })
              : modeLine}
          </span>
        </CardTitle>
      </CardHeader>
      <CardBody className="pt-4">
        <div className="flex flex-wrap justify-between gap-x-8 gap-y-3">
          {stops.map((stop) => (
            <div key={stop.id} className="flex min-w-0 flex-col gap-1.5">
              <span className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={cn('size-2 shrink-0 rounded-full', STOP_DOT[stop.state])}
                />
                <span className="text-text-heading text-[13px] leading-none font-medium">
                  {stop.name}
                </span>
              </span>
              {/* max-w keeps a long caption wrapping under its own stop rather
                  than pushing the row apart; pl-4 hangs it under the name, not
                  the dot. */}
              <span
                className={cn(
                  'max-w-44 pl-4 text-[12px] leading-snug font-light',
                  stop.state === 'current' ? 'text-attention' : 'text-text-muted',
                )}
              >
                {t(`cases.overview.stopRole.${stop.role}`)} · {status(stop)}
              </span>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  )
}
